import Blanket from '@atlaskit/blanket';
import Button from '@atlaskit/button';
import { Box } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import { useState, useEffect, useRef } from 'react';
import { v4 } from 'uuid';

import useLocalStorage from '@/components/common/hooks/useLocalStorage';
import { InstallationStatus, InstallationDetails } from '@/components/common/types';

import { OnboardingLayout } from '../OnboardingLayout';

interface InstallationStatusResponse {
    requestId: string;
    enterpriseId: string;
    teamId: string;
    status: string; // "pending", "success", "failed"
    message: string;
}

export const Installation = () => {
    const [installationStatus, setInstallationStatus] = useState<InstallationStatus>(InstallationStatus.IDLE);
    const [error, setError] = useState<string>("");
    const [installationDetails, setInstallationDetails] = useLocalStorage<InstallationDetails | null>('installation_details', null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Real API function to check installation status
    const checkInstallationStatus = async (requestId: string): Promise<InstallationDetails> => {
        try {
            const response = await fetch(`/gateway/api/rovo-slack-svc/api/v1/installation/status?requestId=${requestId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: InstallationStatusResponse = await response.json();
            
            // Map API status to our internal status
            let mappedStatus: InstallationStatus;
            switch (data.status) {
                case 'pending':
                    mappedStatus = InstallationStatus.INSTALLING;
                    break;
                case 'success':
                    mappedStatus = InstallationStatus.INSTALLED;
                    break;
                case 'failed':
                    mappedStatus = InstallationStatus.ERROR;
                    break;
                default:
                    mappedStatus = InstallationStatus.INSTALLING;
                    break;
            }

            return {
                enterpriseId: data.enterpriseId || 'NO_ENTERPRISE',
                teamId: data.teamId,
                status: mappedStatus,
                error: data.status === 'failed' ? data.message : undefined
            };
        } catch (error) {
            console.error('Error checking installation status:', error);
            throw error;
        }
    };

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    const startPolling = (requestId: string) => {
        // Stop any existing polling
        stopPolling();

        const pollStatus = async () => {
            try {
                console.log('Polling for installation status...');
                const result = await checkInstallationStatus(requestId);

                console.log('Installation status:', result);
                
                if (result.status === InstallationStatus.INSTALLED) {
                    setInstallationStatus(InstallationStatus.INSTALLED);
                    setInstallationDetails(result);
                    stopPolling();
                } else if (result.status === InstallationStatus.ERROR) {
                    setInstallationStatus(InstallationStatus.ERROR);
                    setError(result.error || 'Installation failed');
                    stopPolling();
                }
                // If status is still INSTALLING, continue polling
            } catch (error) {
                console.error('Error checking installation status:', error);
                setInstallationStatus(InstallationStatus.ERROR);
                setError('Error checking installation status. Clear browser cache and try again.');
                stopPolling();
            }
        };

        // Start polling after a 2-second delay, then continue every 2 seconds
        pollingIntervalRef.current = setInterval(pollStatus, 2000);
    };

    const handleInstallApp = () => {
        const requestId = v4();
        setInstallationStatus(InstallationStatus.INSTALLING);
        setError("");
        setInstallationDetails(null);

        // Update browser URL with requestId
        const url = new URL(window.location.href);
        url.searchParams.set('requestId', requestId);
        window.history.replaceState({}, '', url.toString());

        window.open(`${process.env['SLACK_APP_INSTALL_URL']}?requestId=${requestId}`, '_blank', 'noopener,noreferrer');
        
        // Start polling for installation status
        startPolling(requestId);
    };

    const handleNext = () => {
        if (installationStatus === InstallationStatus.INSTALLED && installationDetails) {
            return {
                shouldProceed: true,
                searchParams: {
                    enterpriseId: installationDetails.enterpriseId,
                    teamId: installationDetails.teamId
                }
            };
        }
        return false;
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const existingRequestId = urlParams.get('requestId');
        if (existingRequestId) {
            setInstallationStatus(InstallationStatus.INSTALLING);
            startPolling(existingRequestId);
        }

        // Cleanup polling on unmount
        return () => {
            stopPolling();
        };
    }, []);

    useEffect(() => {
        if(installationDetails 
            && installationDetails.status === InstallationStatus.INSTALLED
            && installationDetails.enterpriseId
            && installationDetails.teamId
        ) {
            localStorage.setItem('installation_details', JSON.stringify({
                enterpriseId: installationDetails.enterpriseId,
                teamId: installationDetails.teamId
            }));
        }
    }, [installationDetails]);

    return (
        <OnboardingLayout 
            currentStep={1} 
            onNext={handleNext}
            onNextLabel="Next"  
            isNextDisabled={() => !(installationStatus === InstallationStatus.INSTALLED)}
        >
            <div>
                <h2>Install the Rovo App for Slack</h2>
                <p>Before you connect your site, you’ll need to install the app. We’ll redirect you to Slack.</p>
                {
                    <Box style={{ marginTop: '24px' }}>
                        {(installationStatus === InstallationStatus.IDLE || installationStatus === InstallationStatus.ERROR) &&
                        <Button appearance="primary" onClick={handleInstallApp}>Add to Slack</Button>}
                    </Box>
                }
                {installationStatus === InstallationStatus.INSTALLING &&
                    <Blanket isTinted>
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Spinner size="large" />
                        </Box>
                    </Blanket>
                }
                {installationStatus === InstallationStatus.INSTALLED &&
                    <Box>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <SectionMessage appearance="success">
                            <p>You’ve installed the Rovo app. Add your site details to continue setup.</p>
                        </SectionMessage>
                        </Box>
                    </Box>
                }
                {installationStatus === InstallationStatus.ERROR &&
                    <Box>
                        <Box style={{ marginBottom: '16px' }}>
                            {error || 'Error checking installation status. Clear browser cache and try again.'}
                        </Box>
                        <Button onClick={() => {
                            setInstallationStatus(InstallationStatus.IDLE);
                            setError("");
                            setInstallationDetails(null);
                        }}>
                            Retry
                        </Button>
                    </Box>
                }
            </div>
        </OnboardingLayout>
    );
};